import { removeStoredMutationsById } from '../graphql/util/removeStoredMutationsById'
import type { Contact } from '@paintscout/api'
import {
  updateCachedContact,
  updateCachedContactPartially,
  useDeleteContactsMutation,
  useSaveContactMutation
} from '@paintscout/api'
import type ApolloCache from '@wora/apollo-cache'
import { useMemo } from 'react'
import { useApolloClient } from '@apollo/react-hooks'

/**
 * Returns common useMutations for saving contacts and handles updating the caches
 */
export function useContactMutations() {
  const [saveContactMutation] = useSaveContactMutation()
  const [deleteContactsMutation] = useDeleteContactsMutation()
  const client = useApolloClient()
  const DEFAULT_REFETCH_QUERIES = ['searchContacts', 'searchContactsWithIntegration']
  const REFRESH_DELAY_MS = 2000

  return useMemo(() => {
    const scheduleDelayedRefetch = (queries: string[]) => {
      if (!queries?.length) {
        return
      }

      setTimeout(() => {
        const queryManager = (client as any).queryManager
        if (!queryManager?.queries) {
          return
        }
        queryManager.queries.forEach(({ observableQuery }: any) => {
          if (!observableQuery) {
            return
          }

          const queryName = observableQuery.queryName
          if (queries.includes(queryName)) {
            observableQuery.refetch().catch(() => {
              // swallow errors from async refetch
            })
          }
        })
      }, REFRESH_DELAY_MS)
    }

    return {
      async saveContact({
        contact,
        skipRefetchQueries,
        refetchQueries
      }: {
        contact: Contact
        skipRefetchQueries?: boolean
        refetchQueries?: string[]
      }) {
        const timeOfSave = Date.now()
        const mutationId = `save-contact-${contact._id}`
        const queriesToRefetch = skipRefetchQueries
          ? []
          : refetchQueries?.length
            ? refetchQueries
            : DEFAULT_REFETCH_QUERIES

        const result = await saveContactMutation({
          variables: {
            contact
          },
          context: {
            __mutation_id__: mutationId
          },
          optimisticResponse: {
            saveContact: {
              __typename: 'Contact' as const,
              _id: contact._id,
              _rev: contact._rev || null, // null is important, if its undefined it will break cache for quote (undefined is not valid graphql value)
              _deleted: contact._deleted ?? null,
              trashed: contact.trashed ?? null,
              trashedBy: contact.trashedBy ?? null,
              deleteDate: contact.deleteDate ?? null,
              type: contact.type ?? null,
              PK: contact.PK ?? null,
              SK: contact.SK ?? null,
              first_name: contact.first_name ?? null,
              last_name: contact.last_name ?? null,
              secondary_first_name: contact.secondary_first_name ?? null,
              secondary_last_name: contact.secondary_last_name ?? null,
              email: contact.email ?? null,
              alternate_email: contact.alternate_email ?? null,
              billingAddress: contact.billingAddress ?? null,
              shippingAddress: contact.shippingAddress ?? null,
              isBillingShipping: contact.isBillingShipping ?? null,
              address: contact.address ?? null,
              city: contact.city ?? null,
              province: contact.province ?? null,
              country: contact.country ?? null,
              postal: contact.postal ?? null,
              geocode: contact.geocode
                ? {
                    __typename: 'ContactGeocode' as const,
                    ...contact.geocode
                  }
                : null,
              phone_number: contact.phone_number ?? null,
              phone_ext: contact.phone_ext ?? null,
              alternate_phone: contact.alternate_phone ?? null,
              alternate_phone_ext: contact.alternate_phone_ext ?? null,
              company: contact.company ?? null,
              companyDocId: contact.companyDocId ?? null,
              contact_type: contact.contact_type ?? null,
              created_by: contact.created_by ?? null,
              created_date: contact.created_date ?? null,
              updated_date: contact.updated_date ?? null,
              preferred_contact_method: contact.preferred_contact_method ?? null,
              tags: contact.tags ?? null,
              files: contact.files ?? null,
              source_id: contact.source_id ?? null,
              source_user_id: contact.source_user_id ?? null,
              source: contact.source ?? null,
              lead_source: contact.lead_source ?? null,
              acquisitionSource: contact.acquisitionSource ?? null,
              internal_note: contact.internal_note ?? null,
              assignedUsers: contact.assignedUsers ?? null,
              owner: contact.owner
                ? {
                    __typename: 'Owner' as const,
                    ...contact.owner
                  }
                : null,
              wisetackPrequal: contact.wisetackPrequal
                ? {
                    __typename: 'WisetackPrequal' as const,
                    ...contact.wisetackPrequal,
                    customerInfo: contact.wisetackPrequal.customerInfo
                      ? {
                          __typename: 'WisetackPrequalCustomer' as const,
                          ...contact.wisetackPrequal.customerInfo
                        }
                      : null
                  }
                : null,
              wisetackLoanApplications: contact.wisetackLoanApplications
                ? contact.wisetackLoanApplications.map((app) => ({
                    __typename: 'WisetackLoanApplication' as const,
                    ...app,
                    refund: app?.refund
                      ? {
                          __typename: 'WisetackLoanApplicationRefund' as const,
                          ...app.refund
                        }
                      : null
                  }))
                : null
            }
          },
          update(cache: ApolloCache, { data }) {
            if (data?.saveContact) {
              updateCachedContact(cache, data.saveContact)
            }
          },
          refetchQueries: queriesToRefetch
        })

        removeStoredMutationsById(client, mutationId, { before: timeOfSave })
        scheduleDelayedRefetch(queriesToRefetch)

        return {
          contact: result.data.saveContact
        }
      },

      async deleteContacts(ids: string[], skipRefetchQueries = false) {
        const queriesToRefetch = skipRefetchQueries ? [] : DEFAULT_REFETCH_QUERIES
        const result = deleteContactsMutation({
          variables: { ids },
          optimisticResponse: {
            deleteContacts: ids.map((id) => ({
              __typename: 'BulkDocsResponseObject',
              _id: id,
              _rev: null,
              error: null
            }))
          },
          update(cache: ApolloCache, { data: _data }) {
            ids.forEach((id) => {
              // we can fake the contact document as just an object with _deleted flag
              updateCachedContactPartially(cache, id, {
                _deleted: true
              })
            })
          },
          refetchQueries: queriesToRefetch
        })

        scheduleDelayedRefetch(queriesToRefetch)

        return result
      }
    }
  }, [client])
}
